import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Table, Input, Tag } from 'antd';
import { parseISO, format } from 'date-fns';
import Rest from 'tools/rest';

const TaskSearchModal = ({ visible, onClose, onSelectTask }) => {
  const [tasks, setTasks] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const fetchSeqRef = useRef(0);

  const loadTasks = (queryRaw) => {
    const trimmed = (queryRaw ?? '').trim();
    if (trimmed && !/^\d+$/.test(trimmed)) {
      fetchSeqRef.current += 1;
      setTasks([]);
      setLoading(false);
      return;
    }

    const params = { page: 1, per: 50 };
    if (trimmed) {
      params.search = { number: parseInt(trimmed, 10) };
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    Rest.get('/api/v1/requests.json', { params })
      .then((response) => {
        if (seq !== fetchSeqRef.current) return;
        setTasks(response.data.requests || []);
      })
      .catch(() => {
        if (seq !== fetchSeqRef.current) return;
        setTasks([]);
      })
      .finally(() => {
        if (seq !== fetchSeqRef.current) return;
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!visible) return;
    setSearch('');
    loadTasks('');
  }, [visible]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      loadTasks(value);
    }, 350);
  };

  const handleClear = () => {
    setSearch('');
    if (timerRef.current) clearTimeout(timerRef.current);
    loadTasks('');
  };

  const emptyText = useMemo(() => {
    if (loading) return 'Загрузка…';
    const t = search.trim();
    if (t && !/^\d+$/.test(t)) return 'Введите только цифры номера задачи';
    if (t && /^\d+$/.test(t)) return 'Задачи с таким номером нет';
    return 'Нет задач';
  }, [search, loading]);

  const columns = [
    {
      title: '№',
      dataIndex: 'id',
      width: 60,
      render: (id) => <strong>#{id}</strong>,
    },
    {
      title: 'Описание',
      dataIndex: 'description',
    },
    {
      title: 'Статус',
      dataIndex: ['request_status', 'name'],
      width: 120,
      render: (status) => {
        const colors = { Выполнена: 'green', 'В работе': 'blue', Новая: 'cyan' };
        return <Tag color={colors[status] || 'default'}>{status || '—'}</Tag>;
      },
    },
    {
      title: 'Дата',
      dataIndex: 'created_at',
      width: 140,
      render: (date) => (date ? format(parseISO(date), 'dd.MM.yyyy HH:mm') : '—'),
    },
  ];

  return (
    <Modal
      title="Выберите задачу для списания"
      open={visible}
      onCancel={onClose}
      footer={null}
      width="70%"
      zIndex={1100}
      destroyOnHidden
    >
      <Input
        placeholder="Номер задачи (например 63)…"
        value={search}
        onChange={handleSearchChange}
        onPressEnter={(e) => loadTasks(e.target.value)}
        style={{ marginBottom: 16 }}
        allowClear
        onClear={handleClear}
      />
      <Table
        columns={columns}
        dataSource={tasks}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText }}
        onRow={(record) => ({
          onClick: () => {
            onSelectTask(record);
          },
          style: { cursor: 'pointer' },
        })}
      />
    </Modal>
  );
};

export default TaskSearchModal;
